import type { Copy } from './pl';

export const en: Copy = {
	meta: {
		title: 'Kamil Pfaff — IT Architect / Consultant',
		description: 'IT consulting, audits and implementation across the whole software lifecycle: code, infrastructure, security and operations. Plus one-to-one mentoring. Kamil Pfaff, an IT architect with hands-on experience in programming and DevOps.',
	},
	nav: {
		home: 'Kamil Pfaff, home',
		index: 'Sheet index',
		sheets: {
			cooperation: 'Working together',
			scope: 'Scope',
			stack: 'Technologies',
			about: 'About',
			contact: 'Contact',
		},
		book: 'Book a consultation',
		menu: 'Menu',
		close: 'Close menu',
		language: 'Wersja polska',
		languageShort: 'PL',
	},
	hero: {
		role: 'IT Architect / Consultant',
		heading: 'From architecture all the way to production.',
		lede: 'Consulting, audits, implementation and mentoring across the whole software lifecycle: from the first line of code, through build and deployment, to production and monitoring.',
		promise: 'One person who understands your system [end to end] and stands behind every recommendation.',
		revision: 'Start here',
		booking: {
			title: 'Propose a time to talk',
			day: 'Day',
			time: 'Time',
			windows: ['8:00–12:00', '12:00–16:00', '16:00–20:00', '20:00–00:00'],
			submit: 'Book a consultation',
			submitExternal: 'Pick a time in my calendar',
			note: 'I will confirm the time by email or phone. Times are Polish time (Europe/Warsaw).',
			orDirect: 'Prefer to talk now?',
		},
		figure: {
			caption: 'Fig. 1',
			title: 'System lifecycle',
			span: 'From plan to monitoring',
			pause: 'Pause animation',
			play: 'Resume animation',
		},
		phases: [
			{ name: 'Plan', line: 'I will help you design the system and choose its architecture.' },
			{ name: 'Code', line: 'I can implement the software you need.' },
			{ name: 'Build', line: 'I will automate your processes.' },
			{ name: 'Deploy', line: 'I will containerize your environment.' },
			{ name: 'Operate', line: 'I will keep your system running in production.' },
			{ name: 'Monitor', line: 'I will set up modern monitoring.' },
		],
		titleBlock: {
			project: 'Project',
			projectValue: 'Your system',
			architect: 'Architect',
			credentials: 'Credentials',
			sheet: 'Sheet',
			scale: 'Scale',
		},
	},
	cooperation: {
		heading: 'Three ways to work together',
		intro: 'We start with a conversation. From there we go as far as you need.',
		first: 'Where we start',
		consult: {
			name: 'Consultation and audit',
			audience: 'For anyone with a problem in IT: a security gap, an unstable server, an application that keeps failing, or a vibe-coded app that just does not work. I get to the bottom of it and find the right solution.',
			steps: [
				{ title: 'Problem', text: 'You tell me what is wrong and what you need.' },
				{ title: 'Diagnosis', text: 'I look for the cause wherever the problem reaches.', areas: ['Code', 'Configuration', 'Infrastructure', 'Security'] },
				{ title: 'Solution', text: 'You get a diagnosis, a concrete solution and next steps.' },
			],
			cta: 'Book a consultation',
		},
		mentor: {
			name: 'Mentoring and training',
			audience: 'For people who want to get into DevOps, infrastructure or programming, or put what they already know in order. One-to-one sessions.',
			topics: ['Linux and administration', 'Docker and Kubernetes', 'CI/CD and GitLab', 'Ansible and automation', 'Backend programming'],
			cta: 'See mentoring',
		},
		build: {
			name: 'Implementation',
			audience: 'Once the direction is clear, I can deliver it: from fixing a single container to running your application in production.',
			partners: 'For complete builds, such as websites or web applications, I work with trusted partners.',
			cta: 'See the scope of work',
		},
	},
	scope: {
		heading: 'Scope of work',
		intro: 'Each item matches a part of the loop in figure 1. Point at an item to see which stage it covers.',
		tools: 'Tools',
		more: 'Details',
		less: 'Collapse',
		items: [
			{
				name: 'IT consulting',
				summary: 'Not sure which technology to pick? I will help you choose architecture and tools that fit your project, its scale and budget.',
				details: ['Choosing between cloud, dedicated servers, VPS or your own machines.', 'Assessing what you have and planning the changes.', "Answers to your team's specific questions."],
				tools: [],
			},
			{
				name: 'Software solutions',
				summary: 'I have experience in backend development. I can write the missing piece or guide you through building the whole application.',
				details: ['PHP backends (Symfony) and integrations.', "Scripts and tools that automate your team's work."],
				tools: ['Symfony', 'Git'],
			},
			{
				name: 'CI/CD automation',
				summary: 'No more manual deployments over SSH. Your application builds, tests and deploys itself.',
				details: [
					'Lower mean time to recovery (MTTR): one-click deployment rollbacks.',
					'Higher deployment frequency through simple automation.',
					'Shorter lead time for changes thanks to fast feedback.',
					'Lower change failure rate: small, well-tested batches.',
				],
				tools: ['GitLab', 'Ansible', 'Docker', 'Kubernetes'],
			},
			{
				name: 'Containers and Kubernetes',
				summary: 'I package your application in containers so it runs the same everywhere. I have experience migrating existing applications to container-based infrastructure.',
				details: [
					'Scalability: a well-prepared application scales easily on Kubernetes or OpenShift, which cuts cloud costs when traffic varies.',
					'Portability: the container holds the whole environment, so starting the app takes a single command.',
					'Maintainability: images are quick to replace, and the orchestrator restarts and recovers the application after failures.',
					'A first step to automation: the same environment at every stage.',
				],
				tools: ['Docker', 'Kubernetes', 'OpenShift'],
			},
			{
				name: 'Infrastructure management',
				summary: 'I choose servers that fit your needs and take care of stability, security and scaling, so you do not have to.',
				details: ['Care of Linux servers and tasks across many machines at once.', 'A solution sized to fit: from a simple website to a Kubernetes cluster.'],
				tools: ['RHEL', 'AWS', 'Ansible'],
			},
			{
				name: 'Monitoring',
				summary: 'An error, a crash, a bad HTTP response, strange traffic? You will be the first to know.',
				details: ['Real-time application error tracking.', 'Metrics, alerts and clear stability dashboards.'],
				tools: ['Sentry', 'Prometheus', 'Grafana'],
			},
		],
	},
	stack: {
		heading: 'Technologies',
		intro: 'I know the whole software lifecycle: writing code, building, deploying, running and monitoring it. Below are the tools I use most and know best.',
		columns: { item: 'Item', name: 'Technology', role: 'Used for', note: 'Notes' },
		rows: [
			{ name: 'RHEL', role: 'Server systems', note: 'Stable, secure servers. Backed by RHCSA and RHCE certifications.' },
			{ name: 'Ansible', role: 'Automation', note: 'Configuration automation, backed by the RHCE certification.' },
			{ name: 'Git', role: 'Version control', note: 'Every programmer uses it. Not everyone uses it well.' },
			{ name: 'GitLab', role: 'DevOps platform', note: 'Including self-managed instances. The backbone of my DevOps transformations.' },
			{ name: 'Docker', role: 'Containerization', note: 'Application isolation and portability.' },
			{ name: 'Kubernetes', role: 'Orchestration', note: 'Scaling and reliable production deployments, OpenShift too.' },
			{ name: 'Sentry', role: 'Error tracking', note: 'Shows the errors that actually matter.' },
			{ name: 'Prometheus', role: 'Metrics and alerts', note: 'Collects metrics in a time-series database.' },
			{ name: 'Grafana', role: 'Visualization', note: 'Clear dashboards from Prometheus and other sources.' },
			{ name: 'AWS', role: 'Cloud', note: 'Scalable cloud infrastructure.' },
			{ name: 'Symfony', role: 'Backend (PHP)', note: 'Over a year of building applications with it.' },
		],
		polyglot: {
			label: 'Note',
			text: 'This is not a closed list. I have programmed in many languages, and the one your project is written in will not hold me back. A language is a tool, like a hammer: what matters is knowing what you are building.',
		},
		seals: {
			heading: 'Certifications',
			issuer: 'Issued by Red Hat',
			rhcsa: 'Red Hat Certified System Administrator',
			rhce: 'Red Hat Certified Engineer',
		},
	},
	about: {
		heading: 'About me',
		paragraphs: [
			'I am Kamil Pfaff, an IT architect and consultant who is still a practitioner. I know the whole software lifecycle: I write code, build CI/CD pipelines, deploy, run servers and set up monitoring.',
			'Day to day I work with Linux and container-based infrastructure (Kubernetes, OpenShift). Self-managed GitLab was the backbone of my largest projects, including full DevOps transformations and migrations of applications to containers.',
			'I like simple solutions to real problems. That is why I only recommend what I can build and maintain myself.',
		],
		photoCaption: 'Photo 1 — Kamil Pfaff',
		photoAlt: 'Kamil Pfaff',
		facts: [
			{ label: 'Role', value: 'IT Architect / Consultant' },
			{ label: 'Experience', value: 'Programming, DevOps, IT administration' },
			{ label: 'Credentials', value: 'RHCSA, RHCE' },
			{ label: 'Based in', value: 'Pszczyna, Poland' },
		],
	},
	contact: {
		heading: 'Book a meeting',
		intro: 'Choose what suits you: a time to talk, a short brief, or a phone call.',
		booking: {
			title: 'Consultation',
			text: 'Propose a day and time, and I will confirm.',
			textExternal: 'Pick a free slot in my calendar.',
			selected: 'Selected time',
			change: 'Change it at the top',
		},
		brief: {
			title: 'Brief',
			text: 'Describe your system or problem in a few words. I will reply to the address you give.',
			name: 'Full name',
			email: 'Email',
			phone: 'Phone (optional)',
			topic: 'What is it about?',
			topics: ['Consultation / audit', 'Mentoring', 'Implementation', 'Other'],
			preferred: 'Preferred time to talk',
			preferredNone: 'None selected',
			preferredPick: 'Pick',
			preferredChange: 'Change',
			message: 'Message',
			messagePlaceholder: 'E.g. "Our app runs in Docker on one VPS and we deploy by hand. We want to fix that."',
			submit: 'Send brief',
			sending: 'Sending…',
			invalid: 'Fix the marked fields before sending.',
			success: 'Thank you, your brief arrived.',
			successNext: 'I will reply to your email address.',
			sendAnother: 'Send another',
			error: 'The form could not be sent. Try again or write to me directly at',
			errors: {
				name: 'Enter your full name.',
				email: 'Enter a valid email address, e.g. jane@company.com.',
				phone: 'Enter a valid number, e.g. +48 600 000 000.',
				message: 'Write a few words about how I can help.',
			},
		},
		direct: {
			title: 'Direct',
			call: 'Call',
			write: 'Email',
		},
	},
	mentoring: {
		meta: {
			title: 'One-to-one IT mentoring — Linux, Docker, Kubernetes, DevOps | Kamil Pfaff',
			description: 'One-to-one mentoring and tutoring: Linux, Docker, Kubernetes, Ansible, CI/CD, programming and cybersecurity basics. Code reviews and audits of your own infrastructure. Kamil Pfaff, RHCSA, RHCE.',
		},
		nav: {
			topics: 'Topics',
			review: 'Review',
			process: 'Process',
			mentor: 'Mentor',
			cta: 'Send a request',
		},
		hero: {
			heading: 'From your first line of code to production.',
			lede: 'One-to-one mentoring in programming, Linux, containers and automation. We talk, fill in the gaps, and I review your code or your server and tell you plainly what needs work. Describe what you need and we will build a concrete plan.',
			modesLegend: 'What do you need?',
			modes: [
				{ name: 'Learn a technology', line: 'Programming, Linux, Docker, Kubernetes, Ansible. From the basics, or from wherever you got stuck.' },
				{ name: 'Code review', line: 'A review of your application code, Dockerfile, pipeline or playbook, followed by a one-to-one discussion. We go through the weak spots, analyse the mistakes and, if needed, put the fixes in together.' },
				{ name: 'Infrastructure audit', line: 'Your VPS, homelab or project server: security, backups, configuration, deployments.' },
				{ name: 'Exam preparation', line: 'Preparing for a certification, or need to pass an exam at university? We check what you know, plan your learning and go through the process together.' },
			],
		},
		rfi: {
			title: 'Request',
			regarding: 'Regarding',
			revision: 'Start here',
			topics: 'Topics',
			topicsHint: 'Tick any',
			topicList: ['Linux', 'Bash', 'Git', 'Docker', 'Kubernetes', 'Ansible', 'CI/CD', 'Monitoring', 'Cybersecurity', 'Networking', 'Programming', 'C#', 'Java', 'JS', 'PHP', 'Other'],
			level: 'Your level',
			levels: ['Just starting', 'School / University', 'Know the basics', 'Use it at work'],
			goal: 'What do you want to achieve?',
			goalPlaceholder: 'E.g. “I want to run my app in Docker on a VPS and understand what I am doing.”',
			known: 'What do you already know? (optional)',
			knownPlaceholder: 'E.g. “I write Python and only use the terminal for git.”',
			link: 'Repository link or a description of your setup (optional)',
			linkPlaceholder: 'https://github.com/… or “Ubuntu VPS, Nginx, two apps”',
			name: 'Name',
			email: 'Email',
			phone: 'Phone (optional)',
			submit: 'Send request',
			sending: 'Sending…',
			note: 'Online, one-to-one. We agree scope, timing and price after your request.',
			success: 'Thank you, your request arrived. I will reply with a proposal to the address you gave.',
			sendAnother: 'Send another',
			error: 'The request could not be sent. Try again or write directly to',
			errors: {
				name: 'Enter your name.',
				email: 'Enter a valid email address, e.g. jane@mail.com.',
				phone: 'Enter a valid number, e.g. +48 600 000 000.',
				goal: 'Write a few words about what you want to learn or what I should review.',
			},
			summary: {
				request: 'Request',
				topics: 'Topics',
				level: 'Level',
				goal: 'Goal',
				known: 'Knows',
				link: 'Link / setup',
			},
		},
		topics: {
			heading: 'Topics',
			intro: 'I teach what I use every day. Sessions are shaped around your level and your project, not a ready-made course.',
			columns: { item: 'Item', name: 'Topic', scope: 'What we cover', basis: 'Basis' },
			practice: 'Practice',
			rows: [
				{ name: 'Linux and the shell', scope: 'Users and permissions, systemd, packages, logs, networking, Bash scripting.', basis: 'RHCSA' },
				{ name: 'Git', scope: 'Branches, merge and rebase, a readable history, teamwork, undoing mistakes.', basis: '' },
				{ name: 'Docker', scope: 'Images and Dockerfiles, Compose, networks and volumes, small and safe images.', basis: '' },
				{ name: 'Kubernetes', scope: 'Pods, deployments, services, ingress, config and secrets, OpenShift.', basis: '' },
				{ name: 'Ansible', scope: 'Playbooks, roles, inventories, idempotency, automating many servers.', basis: 'RHCE' },
				{ name: 'CI/CD', scope: 'GitLab pipelines, tests, building images, automated deployments.', basis: '' },
				{ name: 'Monitoring', scope: 'Metrics in Prometheus, Grafana dashboards, error tracking in Sentry, alerts.', basis: '' },
				{ name: 'Cybersecurity basics', scope: 'SSH and keys, firewalls, updates, secrets, least privilege, backups.', basis: '' },
				{ name: 'Programming', scope: 'Fundamentals, PHP backend (Symfony), automation scripts, readable code.', basis: '' },
			],
		},
		review: {
			heading: 'What a review looks like',
			intro: 'You don’t get a vague “looks fine”. Every comment has a number, a place in the code, a reason and a fix. I review pipelines, playbooks and server configuration the same way.',
			caption: 'Fig. 1',
			title: 'Example review of a docker-compose.yml file',
			sample: 'Example',
			notes: [
				{ title: '“latest” changes without you knowing', fix: 'Pin the version, e.g. postgres:16. Upgrading becomes your decision, not a surprise after a restart.' },
				{ title: 'The password in this file ends up in git history', fix: 'Move it to an .env file or secrets, add .env to .gitignore and change the password, because the old one has leaked.' },
				{ title: 'The database is open to the internet', fix: 'Remove the 5432 port mapping. The app reaches the database over the internal Compose network.' },
				{ title: 'Nothing comes back after a server restart', fix: 'Add restart: unless-stopped and a healthcheck so the containers recover on their own.' },
			],
		},
		process: {
			heading: 'How it works',
			steps: [
				{ name: 'Request', line: 'You send the form at the top of this page: what you need and where you are now.' },
				{ name: 'Proposal', line: 'I reply with a plan: scope, format, timing and price. Nothing is binding until you agree.' },
				{ name: 'Session', line: 'We meet online, one-to-one, with screen sharing. We work on your code and your servers.' },
				{ name: 'Notes', line: 'You get a summary, comments and next steps, so you can keep going on your own.' },
			],
			facts: [
				{ label: 'Format', value: 'Online, one-to-one' },
				{ label: 'Meeting', value: 'Screen sharing' },
				{ label: 'Language', value: 'Polish or English' },
				{ label: 'Price', value: 'Agreed after your request' },
			],
		},
		mentor: {
			heading: 'Who teaches',
			paragraphs: [
				'My name is Kamil Pfaff. I am an IT architect and DevOps engineer. Day to day I work with Linux and container-based infrastructure, automation and CI/CD.',
				'I teach the same things I deliver for clients, so sessions are built on real problems, not slides. Red Hat certifications back my Linux and Ansible skills.',
			],
			business: 'Running a company and need a consultation or an implementation?',
			businessLink: 'See services for companies',
			direct: 'Prefer to write or call?',
		},
	},
	footer: {
		companyLabel: 'Company',
		company: 'Kamil Pfaff Konsulting IT',
		address: 'Kasztelańska 4, 43-200 Pszczyna, Poland',
		revision: 'Revision',
		revisionValue: 'B — new site',
		top: 'Back to top',
	},
};
